import { useId, useMemo } from 'react';
import { useMeasuredWidth } from '../../hooks/useMeasuredWidth.js';

export interface AreaChartDatum {
  x: string;
  y: number;
}

export interface DefaultAreaChartProps {
  data: AreaChartDatum[];
  height?: number;
  /** Formatter for Y-axis + last-point label. Defaults to a `0,0` integer. */
  formatY?: (n: number) => string;
}

type Point = readonly [number, number];

interface Geometry {
  linePath: string;
  areaPath: string;
  last: Point | null;
  xLabels: { x: number; text: string }[];
  yLabels: { y: number; text: string }[];
  lo: number;
  hi: number;
  pathLength: number;
}

const DEFAULT_FORMAT_Y = (n: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

const PADDING_LEFT = 40;
const PADDING_RIGHT = 16;
const PADDING_TOP = 14;
const PADDING_BOTTOM = 28;

/**
 * Default-theme hero chart (also the renderer for every non-Luce theme —
 * Neon, Obsidian, etc.). Soft-green area fill under a brand-green stroke;
 * draws on mount via stroke-dashoffset. Pure SVG, no chart library. Reads
 * `--evi-accent` + `--evi-border` + `--evi-text-mute` so it reskins cleanly.
 *
 * Responsive: the SVG draws at the container's MEASURED pixel width
 * (`viewBox` width === measured width, so 1 unit = 1 px). It previously
 * stretched a fixed 600-unit viewBox to fill via
 * `preserveAspectRatio="none"`, which smeared the stroke + labels and
 * clipped the last x-label on wide cards (the full-width Reports card was
 * >2× the viewBox). Geometry recomputes when the width changes; the height
 * stays fixed so the chart reads the same in a narrow dashboard cell or a
 * wide Reports card. The last x-label is end-anchored (first is
 * start-anchored) so the edge labels always sit inside the drawing area.
 */
export function DefaultAreaChart({
  data,
  height = 220,
  formatY = DEFAULT_FORMAT_Y,
}: DefaultAreaChartProps) {
  const id = useId().replace(/:/g, '');
  const [wrapRef, width] = useMeasuredWidth<HTMLDivElement>();
  const geom = useMemo<Geometry>(
    () => buildGeometry(data, width, height, formatY),
    [data, width, height, formatY],
  );

  const { linePath, areaPath, last, xLabels, yLabels, pathLength } = geom;
  const plotRight = width - PADDING_RIGHT;

  // Reserve the height on the wrapper so the card doesn't reflow when the
  // SVG mounts on the next frame (once the width measurement lands).
  return (
    <div ref={wrapRef} style={{ width: '100%', height }}>
      {width > 0 && (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label="Trend over time"
        >
          <defs>
            <linearGradient id={`evi-area-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--evi-accent)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--evi-accent)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Horizontal Y-axis gridlines */}
          {yLabels.map((g, i) => (
            <line
              key={i}
              x1={PADDING_LEFT}
              y1={g.y}
              x2={plotRight}
              y2={g.y}
              stroke="var(--evi-border)"
              strokeWidth="0.5"
              strokeDasharray={i === yLabels.length - 1 ? 'none' : '2 4'}
            />
          ))}

          {/* Y-axis labels */}
          {yLabels.map((g, i) => (
            <text
              key={i}
              x={PADDING_LEFT - 8}
              y={g.y + 3}
              textAnchor="end"
              fontSize="10"
              fill="var(--evi-text-mute)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {g.text}
            </text>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill={`url(#evi-area-${id})`} />

          {/* Stroke — drawn on mount */}
          <path
            d={linePath}
            fill="none"
            stroke="var(--evi-accent)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: pathLength,
              strokeDashoffset: pathLength,
              animation: `evi-line-draw-${id} 1.6s var(--evi-motion-fill) 0.3s forwards`,
            }}
          />

          <style>{`
            @keyframes evi-line-draw-${id} { to { stroke-dashoffset: 0; } }
            @keyframes evi-dot-appear-${id} { to { opacity: 1; } }
          `}</style>

          {/* Last-point marker */}
          {last && (
            <circle
              cx={last[0]}
              cy={last[1]}
              r="4"
              fill="var(--evi-surface)"
              stroke="var(--evi-accent)"
              strokeWidth="2"
              style={{
                opacity: 0,
                animation: `evi-dot-appear-${id} 0.3s ease 1.9s forwards`,
              }}
            />
          )}

          {/* X-axis labels — first start-anchored, last end-anchored, the
              rest centered, so the edge labels stay inside the drawing
              area at any width (fixes the clipped "TODAY" / "NOW"). */}
          {xLabels.map((g, i) => (
            <text
              key={i}
              x={g.x}
              y={height - 8}
              textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
              fontSize="10"
              fill="var(--evi-text-mute)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {g.text}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

function buildGeometry(
  data: AreaChartDatum[],
  width: number,
  height: number,
  formatY: (n: number) => string,
): Geometry {
  if (data.length === 0 || width <= 0) {
    return { linePath: '', areaPath: '', last: null, xLabels: [], yLabels: [], lo: 0, hi: 0, pathLength: 0 };
  }

  const ys = data.map((d) => d.y);
  const lo = Math.min(...ys, 0);
  const hiRaw = Math.max(...ys, 1);
  // Pad the top by 10% so the peak doesn't touch the chart ceiling.
  const hi = hiRaw + (hiRaw - lo) * 0.1;
  const range = hi - lo || 1;

  const plotLeft = PADDING_LEFT;
  const plotRight = width - PADDING_RIGHT;
  const plotTop = PADDING_TOP;
  const plotBottom = height - PADDING_BOTTOM;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const points: Point[] = data.map((d, i) => {
    const x = plotLeft + (i / Math.max(data.length - 1, 1)) * plotW;
    const y = plotBottom - ((d.y - lo) / range) * plotH;
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const area = `${line} L ${last[0].toFixed(1)} ${plotBottom} L ${first[0].toFixed(1)} ${plotBottom} Z`;

  let pathLength = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    pathLength += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }

  // Y ticks — 3 lines (top, middle, baseline).
  const yLabels = [0, 0.5, 1].map((frac) => ({
    y: plotTop + frac * plotH,
    text: formatY(hi - frac * range),
  }));

  // X ticks — first, middle, last.
  const lastIdx = data.length - 1;
  const xIndices = lastIdx <= 0 ? [0] : [0, Math.floor(lastIdx / 2), lastIdx];
  const xLabels = xIndices.map((idx) => {
    const point = points[idx]!;
    const datum = data[idx]!;
    return { x: point[0], text: datum.x };
  });

  return {
    linePath: line,
    areaPath: area,
    last,
    xLabels,
    yLabels,
    lo,
    hi,
    pathLength: Math.ceil(pathLength),
  };
}
